import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";

export const expandVerseRangeNumbers = (verseRange) => {
  if (!verseRange && verseRange !== 0) return [];
  const raw = String(verseRange).trim();
  if (!raw) return [];
  if (raw.includes("-")) {
    const [startRaw, endRaw] = raw.split("-");
    const start = parseInt(startRaw, 10);
    const end = parseInt(endRaw, 10);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      const result = [];
      const cappedEnd = Math.min(end, start + 200);
      for (let v = start; v <= cappedEnd; v += 1) {
        result.push(v);
      }
      return result;
    }
  }
  const single = parseInt(raw, 10);
  return Number.isFinite(single) ? [single] : [];
};

export const buildVerseNotesMap = (notes = [], currentUid = "") => {
  if (!currentUid || !Array.isArray(notes)) return {};
  const map = {};
  notes.forEach((note) => {
    if (!note) return;
    if (note.userId && note.userId !== currentUid) return;
    const verses = expandVerseRangeNumbers(note.verseRange);
    verses.forEach((v) => {
      if (!map[v]) {
        map[v] = [];
      }
      map[v].push(note);
    });
  });
  return map;
};

export const getNoteReviewStatusChip = (note, isEn = false) => {
  const status =
    note?.reviewStatus ||
    (note?.isPublic ? "approved" : "private");

  if (status === "pending") {
    return {
      label: isEn ? "Pending Review" : "审核中",
      color: "warning",
    };
  }
  if (status === "approved") {
    return {
      label: isEn ? "Public (Approved)" : "已公开",
      color: "success",
    };
  }
  if (status === "rejected") {
    return {
      label: isEn ? "Rejected" : "未通过",
      color: "error",
    };
  }
  return {
    label: isEn ? "Private" : "仅自己可见",
    color: "default",
  };
};

const VerseCommentsDrawer = ({
  open,
  onClose,
  bookName = "",
  chapterNumber = 1,
  verse = 1,
  verseText = "",
  myNotes = [],
  publicNotes = [],
  onAddNote,
  onEditNote,
  onDeleteNote,
  onRequestPublic,
  onWithdrawPublic,
  edition,
  isMobile = false,
}) => {
  const isEn = edition?.id === "en";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 400,
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ChatBubbleOutlineIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.02rem" }}>
            {bookName} {chapterNumber}:{verse} · {isEn ? "Verse Notes" : "节评"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Verse quote */}
      {verseText && (
        <Box
          sx={{
            mx: 2,
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "action.hover",
            borderLeft: "3px solid",
            borderColor: "primary.main",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontStyle: "italic",
              color: "text.primary",
              lineHeight: 1.6,
            }}
          >
            “{verseText}”
          </Typography>
        </Box>
      )}

      <Divider />

      {/* Notes list */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 1.25, color: "text.secondary" }}
        >
          {isEn
            ? `My Reflections (${myNotes.length})`
            : `我的节评 (${myNotes.length})`}
        </Typography>

        {myNotes.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {isEn
                ? "You have no reflections on this verse yet."
                : "你还没有在这一节写下节评"}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2.5 }}>
            {myNotes.map((note) => {
              const statusChip = getNoteReviewStatusChip(note, isEn);
              const status =
                note?.reviewStatus || (note?.isPublic ? "approved" : "private");

              return (
                <Paper
                  key={note.id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <Chip
                        label={statusChip.label}
                        color={statusChip.color}
                        size="small"
                        variant={statusChip.color === "default" ? "outlined" : "filled"}
                        sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
                      />
                      {note.verseRange && String(note.verseRange) !== String(verse) && (
                        <Chip
                          label={`第 ${note.verseRange} 节`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.68rem" }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                      {onEditNote && (
                        <Tooltip title={isEn ? "Edit" : "编辑"}>
                          <IconButton
                            size="small"
                            aria-label={isEn ? "Edit note" : "编辑笔记"}
                            onClick={() => onEditNote(note)}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDeleteNote && (
                        <Tooltip title={isEn ? "Delete" : "删除"}>
                          <IconButton
                            size="small"
                            aria-label={isEn ? "Delete note" : "删除笔记"}
                            onClick={() => onDeleteNote(note)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {note.title && (
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.88rem" }}
                    >
                      {note.title}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      fontSize: "0.85rem",
                    }}
                  >
                    {note.content}
                  </Typography>

                  {status === "rejected" && note.reviewNote && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.75 }}
                    >
                      {isEn ? "Reason: " : "未通过原因："}
                      {note.reviewNote}
                    </Typography>
                  )}

                  {(onRequestPublic || onWithdrawPublic) && (
                    <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                      {status === "private" || status === "rejected" ? (
                        onRequestPublic && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => onRequestPublic(note)}
                            sx={{ fontSize: "0.72rem", textTransform: "none", py: 0 }}
                          >
                            {isEn ? "Submit for Public Review" : "申请公开展示（需后台审核）"}
                          </Button>
                        )
                      ) : (
                        onWithdrawPublic && (
                          <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            onClick={() => onWithdrawPublic(note)}
                            sx={{ fontSize: "0.72rem", textTransform: "none", py: 0 }}
                          >
                            {isEn ? "Make Private" : "撤回公开 / 转为私有"}
                          </Button>
                        )
                      )}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Approved public notes section (when provided) */}
        {Array.isArray(publicNotes) && publicNotes.length > 0 && (
          <Box>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1.25, color: "text.secondary" }}
            >
              {isEn
                ? `Community Reflections (${publicNotes.length})`
                : `弟兄姊妹的分享（已审核 · ${publicNotes.length}）`}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {publicNotes.map((pub) => (
                <Paper
                  key={pub.id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
                  >
                    {pub.userDisplayName || "主内肢体"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}
                  >
                    {pub.content || pub.title}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Divider />

      {/* Footer action */}
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<AddCommentOutlinedIcon />}
          onClick={() => onAddNote && onAddNote(verse, verseText)}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {isEn ? "Write Reflection on This Verse" : "写下本节感动"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default VerseCommentsDrawer;
